/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Download, FileText, Lock, Award, ClipboardCheck, Target, Trophy } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Progress } from "./components/ui/progress";
import { toast } from "sonner";
import { QuizRunner } from "./components/QuizRunner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("mgbApprenantToken")}` });

const downloadBlob = async (url, fallback) => {
  const res = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
  const match = (res.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href; a.download = match ? match[1] : fallback;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
};

const QuizBadge = ({ quiz, seuil }) => {
  if (!quiz) return null;
  if (quiz.passed) return <Badge className="bg-green-600 text-white text-[10px]">Quiz validé · {quiz.best_pct} %</Badge>;
  if (quiz.attempts > 0) return <Badge variant="outline" className="border-red-300 text-red-600 text-[10px]">Dernier essai {quiz.last_pct} % (seuil {seuil} %)</Badge>;
  return <Badge variant="outline" className="text-[10px]">Quiz · {quiz.nb_questions} questions</Badge>;
};

const ModuleCard = ({ m, index, seuil, onToggle, onQuiz, busy }) => (
  <div className={`p-4 rounded-xl border bg-white transition-colors ${m.done ? "border-green-300" : "border-gray-200"}`} data-testid={`module-${m.id}`}>
    <div className="flex items-start gap-3">
      <button type="button" onClick={() => onToggle(m)} disabled={busy} className="mt-0.5 shrink-0" aria-label="Marquer terminé" data-testid={`module-toggle-${m.id}`}>
        {m.done ? <CheckCircle2 className="h-6 w-6 text-green-600" /> : <Circle className="h-6 w-6 text-gray-300 hover:text-[#F07B1D]" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-heading font-bold text-[#003087]">{m.title}</p>
          {m.duration && <Badge variant="outline" className="text-[10px]">{m.duration}</Badge>}
          <QuizBadge quiz={m.quiz} seuil={seuil} />
        </div>
        <ul className="mt-2 space-y-1 text-sm text-gray-700 list-disc pl-5">
          {m.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
        {m.resources.length > 0 && (
          <div className="mt-3 grid gap-2">
            {m.resources.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => downloadBlob(`${API}/apprenant/resources/${r.id}/download`, r.original_filename).catch(() => toast.error("Téléchargement impossible"))}
                className="flex items-center gap-2 text-sm text-left p-2 rounded-md border border-gray-200 hover:border-[#F07B1D]/50 hover:bg-[#F07B1D]/5"
                data-testid={`module-resource-${r.id}`}
              >
                <FileText className="h-4 w-4 text-[#F07B1D] shrink-0" />
                <span className="flex-1 truncate">{r.title}</span>
                <Download className="h-4 w-4 text-gray-400" />
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant={m.done ? "outline" : "default"} onClick={() => onToggle(m)} disabled={busy}
            className={m.done ? "" : "bg-[#003087] hover:bg-[#002366] text-white"} data-testid={`module-done-btn-${m.id}`}>
            {m.done ? "Marquer comme non terminé" : "Marquer le module terminé"}
          </Button>
          {m.quiz && (
            <Button size="sm" variant="outline" onClick={() => onQuiz(m.quiz.id)} className="border-[#F07B1D] text-[#F07B1D] hover:bg-[#F07B1D]/10" data-testid={`module-quiz-btn-${m.id}`}>
              <ClipboardCheck className="h-4 w-4 mr-1" /> {m.quiz.passed ? "Refaire le quiz" : `Quiz du module ${index + 1}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default function ApprenantFormationPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!localStorage.getItem("mgbApprenantToken")) { navigate("/apprenant/login"); return; }
    try {
      const res = await axios.get(`${API}/apprenant/formations/${code}`, { headers: authHeaders() });
      setF(res.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/apprenant/login");
      else toast.error(e.response?.data?.detail || "Formation introuvable");
    }
  }, [code, navigate]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (m) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/apprenant/formations/${code}/modules/${m.id}`, { done: !m.done }, { headers: authHeaders() });
      setF(res.data);
      if (!m.done) toast.success("Module terminé");
      if (res.data.certificat?.available && !f.certificat?.available) toast.success("Félicitations ! Votre certificat de réalisation est disponible.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mise à jour impossible");
    } finally { setBusy(false); }
  };

  if (!f) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Chargement…</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="apprenant-formation-page">
      <header className="bg-[#003087] text-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/apprenant" className="text-white/80 hover:text-white flex items-center gap-1 text-sm" data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4" /> Mon espace
          </Link>
          <span className="text-white/40">·</span>
          <p className="font-heading font-bold truncate">{f.code} — {f.title}</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {quizId ? (
          <QuizRunner quizId={quizId} onClose={() => { setQuizId(null); load(); }} onResult={(r) => setF(r.formation)} />
        ) : (
          <>
            <Card className="border-l-4 border-l-[#F07B1D]">
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-[#003087] text-white">{f.code}</Badge>
                      <Badge variant="outline" className="text-[10px]">{f.type === "selfservice" ? "Self-service" : "Présentiel / Mixte"}</Badge>
                      <Badge variant="outline" className="text-[10px]">{f.hours} h</Badge>
                    </div>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#003087]">{f.title}</h1>
                    <p className="text-sm text-gray-600 mt-1">{f.nb_modules_done}/{f.nb_modules} modules terminés · quiz final {f.final_quiz?.passed ? "validé" : "à valider"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-heading font-bold text-[#F07B1D]" data-testid="formation-progress-pct">{f.pct}%</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">progression</p>
                  </div>
                </div>
                <Progress value={f.pct} className="h-3 mt-4" />
              </CardContent>
            </Card>

            {f.objectifs.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#003087] text-base flex items-center gap-2"><Target className="h-4 w-4" /> Objectifs pédagogiques</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="grid md:grid-cols-2 gap-2 text-sm text-gray-700">
                    {f.objectifs.map((o, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#2196F3] shrink-0 mt-0.5" />{o}</li>)}
                  </ul>
                </CardContent>
              </Card>
            )}

            <div className="space-y-3">
              <h2 className="font-heading text-lg font-bold text-[#003087]">Modules</h2>
              {f.modules.map((m, i) => <ModuleCard key={m.id} m={m} index={i} seuil={f.seuil} onToggle={toggle} onQuiz={setQuizId} busy={busy} />)}
            </div>

            {f.general_resources.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#003087] text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Supports de la formation</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-2 md:grid-cols-2">
                  {f.general_resources.map((r) => (
                    <button key={r.id} type="button" onClick={() => downloadBlob(`${API}/apprenant/resources/${r.id}/download`, r.original_filename).catch(() => toast.error("Téléchargement impossible"))}
                      className="flex items-center gap-2 text-sm text-left p-3 rounded-md border border-gray-200 hover:border-[#F07B1D]/50 bg-white" data-testid={`general-resource-${r.id}`}>
                      <FileText className="h-4 w-4 text-[#F07B1D] shrink-0" /><span className="flex-1 truncate">{r.title}</span><Download className="h-4 w-4 text-gray-400" />
                    </button>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card className={`border-2 ${f.certificat?.available ? "border-green-500" : f.final_unlocked ? "border-[#F07B1D]" : "border-gray-200"}`} data-testid="final-quiz-card">
              <CardHeader>
                <CardTitle className="text-[#003087] flex items-center gap-2">
                  {f.certificat?.available ? <Award className="h-5 w-5 text-green-600" /> : f.final_unlocked ? <Trophy className="h-5 w-5 text-[#F07B1D]" /> : <Lock className="h-5 w-5 text-gray-400" />}
                  Quiz final de validation
                </CardTitle>
                <CardDescription>
                  {f.final_quiz ? `${f.final_quiz.nb_questions} questions · réussite à partir de ${f.seuil} % · tentatives illimitées.` : "Quiz final en préparation."}
                  {f.type === "selfservice" && " À 100 % (modules + quiz final réussi), votre certificat de réalisation est généré automatiquement et envoyé par e-mail."}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <QuizBadge quiz={f.final_quiz} seuil={f.seuil} />
                {f.final_quiz && (
                  <Button onClick={() => setQuizId(f.final_quiz.id)} disabled={!f.final_unlocked}
                    className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" data-testid="final-quiz-btn">
                    <ClipboardCheck className="h-4 w-4 mr-1" /> {f.final_quiz.passed ? "Refaire le quiz final" : f.final_unlocked ? "Passer le quiz final" : "Terminez tous les modules"}
                  </Button>
                )}
                {f.certificat?.available && (
                  <Button variant="outline" onClick={() => downloadBlob(`${API}/apprenant/documents/${f.certificat.dossier_id}/certificat.pdf`, "MGB-School_Certificat.pdf").catch(() => toast.error("Téléchargement impossible"))}
                    className="border-green-600 text-green-700 hover:bg-green-50 font-bold" data-testid="certificat-download-btn">
                    <Award className="h-4 w-4 mr-1" /> Télécharger mon certificat
                  </Button>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
